/**
 * Leaderboard Component
 *
 * Displays player rankings with animations for position changes.
 */

import type { LeaderboardEntry } from '../../../types/liveQuiz';

interface LeaderboardProps {
  entries: LeaderboardEntry[];
  maxEntries?: number;
  highlightParticipantId?: string;
  showMovement?: boolean;
  compact?: boolean;
}

export function Leaderboard({
  entries,
  maxEntries = 5,
  highlightParticipantId,
  showMovement = true,
  compact = false,
}: LeaderboardProps) {
  const displayedEntries = entries.slice(0, maxEntries);

  const getRankEmoji = (rank: number) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return null;
    }
  };

  const getMovementIndicator = (entry: LeaderboardEntry) => {
    if (!showMovement || entry.previousRank === undefined) return null;

    const diff = entry.previousRank - entry.rank;
    if (diff > 0) {
      return (
        <span className="text-green-400 text-sm flex items-center">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {diff}
        </span>
      );
    } else if (diff < 0) {
      return (
        <span className="text-red-400 text-sm flex items-center">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {Math.abs(diff)}
        </span>
      );
    }
    return null;
  };

  if (displayedEntries.length === 0) {
    return (
      <div className="text-center text-gray-400 py-4">
        No scores yet
      </div>
    );
  }

  if (compact) {
    return (
      <div className="space-y-2">
        {displayedEntries.map((entry) => (
          <div
            key={entry.participantId}
            className={`flex items-center justify-between px-3 py-2 rounded-lg ${
              entry.participantId === highlightParticipantId
                ? 'bg-purple-500/30 border border-purple-400'
                : 'bg-white/5'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-gray-400 w-6">
                {getRankEmoji(entry.rank) || `#${entry.rank}`}
              </span>
              <span className="text-sm font-medium text-white truncate max-w-[100px]">
                {entry.nickname}
              </span>
            </div>
            <span className="text-sm font-bold text-purple-300">
              {entry.score.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {displayedEntries.map((entry, index) => {
        const isHighlighted = entry.participantId === highlightParticipantId;
        const isTop3 = entry.rank <= 3;

        return (
          <div
            key={entry.participantId}
            className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
              isHighlighted
                ? 'bg-gradient-to-r from-purple-600/40 to-pink-600/40 border-2 border-purple-400 scale-105'
                : isTop3
                ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30'
                : 'bg-white/5 border border-white/10'
            }`}
            style={{
              animationDelay: `${index * 100}ms`,
            }}
          >
            {/* Rank */}
            <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
              isTop3 ? 'text-3xl' : 'bg-white/10 text-xl font-bold text-white'
            }`}>
              {getRankEmoji(entry.rank) || entry.rank}
            </div>

            {/* Avatar & Name */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {entry.avatar && (
                  <span className="text-2xl">{entry.avatar}</span>
                )}
                <span className={`font-semibold truncate ${
                  isHighlighted ? 'text-white text-lg' : 'text-white'
                }`}>
                  {entry.nickname}
                </span>
                {getMovementIndicator(entry)}
              </div>
              {entry.streak > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-orange-400 text-sm">🔥</span>
                  <span className="text-sm text-orange-300">{entry.streak} streak</span>
                </div>
              )}
            </div>

            {/* Score */}
            <div className="flex-shrink-0 text-right">
              <div className={`font-bold ${
                isTop3 ? 'text-2xl text-yellow-300' : 'text-xl text-purple-300'
              }`}>
                {entry.score.toLocaleString()}
              </div>
              <div className="text-xs text-gray-400">
                {entry.correctAnswers} correct
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Leaderboard;
